import { useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, Wrench, FileText, UserCircle, Plus, Download, Mail, Phone } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Shell } from "@/components/Shell";
import { PageHeader, Loading, StatusBadge, EmptyState, StatCard } from "@/components/common";
import { FormDialog } from "@/components/FormDialog";
import { AccountCard } from "@/components/AccountCard";
import { api, useFetch, errMsg, money, fmtDate, openFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function TenancyCard({ t }) {
  const cur = t.business?.currency || "GBP";
  const next = t.rent.find((r) => r.status !== "paid");
  return (
    <div className="mb-10" data-testid={`renter-tenancy-${t.id}`}>
      <div className="panel p-0 overflow-hidden grid md:grid-cols-3 mb-6">
        {t.property?.image_url && <img src={t.property.image_url} alt="" className="w-full h-48 md:h-full object-cover" />}
        <div className={`p-6 ${t.property?.image_url ? "md:col-span-2" : "md:col-span-3"}`}>
          <div className="flex items-center gap-2 mb-2"><span className="eyebrow">Your home</span><StatusBadge status={t.status} /></div>
          <h2 className="text-2xl sm:text-3xl font-extrabold" data-testid="renter-property-name">{t.property?.name}{t.unit && ` · ${t.unit.name}`}</h2>
          <p className="text-slate-500 mt-1">{[t.property?.address_line, t.property?.city, t.property?.postcode].filter(Boolean).join(", ")}</p>
          <div className="mt-4 text-sm grid sm:grid-cols-2 gap-2">
            <div>Tenancy: <b>{fmtDate(t.start_date)} – {fmtDate(t.end_date)}</b></div>
            <div>Deposit: <b>{money(t.deposit_amount, cur)}</b> {t.deposit_scheme && `(${t.deposit_scheme})`}</div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 text-sm" data-testid="renter-landlord-contact">
            <div className="font-semibold">Managed by {t.business?.name}</div>
            <div className="flex flex-wrap gap-4 mt-1 text-slate-600">
              {t.business?.email && <a href={`mailto:${t.business.email}`} className="flex items-center gap-1 hover:text-slate-900"><Mail className="w-4 h-4" />{t.business.email}</a>}
              {t.business?.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{t.business.phone}</span>}
            </div>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Rent" value={money(t.rent_amount, cur)} hint={t.rent_frequency} testid="renter-rent-amount" />
        <StatCard label="Next payment" value={next ? money(next.amount - (next.paid_amount || 0), cur) : "—"} hint={next ? `due ${fmtDate(next.due_date)}` : "All paid"} testid="renter-next-payment" />
        <StatCard label="Balance overdue" value={money(t.balance_due, cur)} tone={t.balance_due > 0 ? "red" : "green"} testid="renter-balance" />
      </div>
      <div className="panel p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Due date</TableHead><TableHead>Amount</TableHead><TableHead>Paid</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {t.rent.map((r) => (
              <TableRow key={r.id}><TableCell>{fmtDate(r.due_date)}</TableCell><TableCell className="font-mono">{money(r.amount, cur)}</TableCell>
                <TableCell className="font-mono">{money(r.paid_amount, cur)}</TableCell><TableCell><StatusBadge status={r.status} /></TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Overview() {
  const { session } = useAuth();
  const { data } = useFetch("/renter/overview");
  return (
    <div data-testid="renter-overview-page">
      <PageHeader eyebrow="Renter portal" title={`Hi ${session.user.name?.split(" ")[0] || ""}`} subtitle="Your tenancy, rent and landlord contact details." />
      {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={Home} title="No tenancy found" text="Your landlord hasn't linked a tenancy to this account yet." /> : data.map((t) => <TenancyCard key={t.id} t={t} />)}
    </div>
  );
}

function Repairs() {
  const { data, reload } = useFetch("/renter/maintenance");
  const { data: tens } = useFetch("/renter/overview");
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="renter-repairs-page">
      <PageHeader eyebrow="Repairs" title="Report a repair" subtitle="Tell your landlord what needs fixing and follow progress here."
        actions={<Button onClick={() => setOpen(true)} disabled={!tens?.length} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="renter-report-button"><Plus className="w-4 h-4 mr-1" />Report an issue</Button>} />
      {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={Wrench} title="No repairs reported" text="Everything working? Great." /> : (
        <div className="space-y-3">
          {data.map((m) => (
            <div key={m.id} className="panel flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`renter-maint-${m.id}`}>
              <div>
                <div className="font-semibold">{m.title}</div>
                <div className="text-sm text-slate-500">{m.description}</div>
                <div className="text-xs text-slate-400 mt-1">Reported {fmtDate(m.created_at)}</div>
                {(m.notes || []).slice(-1).map((n, i) => <div key={i} className="text-xs mt-2 bg-slate-50 rounded p-2">Latest update: {n.text}</div>)}
              </div>
              <div className="flex gap-2"><StatusBadge status={m.priority} /><StatusBadge status={m.status} testid={`renter-maint-status-${m.id}`} /></div>
            </div>
          ))}
        </div>
      )}
      <FormDialog open={open} onOpenChange={setOpen} title="Report an issue" testid="renter-maint-form"
        fields={[
          { name: "tenancy_id", label: "Property", type: "select", required: true, default: tens?.[0]?.id, options: (tens || []).map((t) => ({ value: t.id, label: t.property?.name })), full: true },
          { name: "title", label: "What's wrong?", required: true, full: true, placeholder: "e.g. Boiler not heating water" },
          { name: "priority", label: "How urgent?", type: "select", default: "medium", options: [{ value: "low", label: "Low" }, { value: "medium", label: "Medium" }, { value: "high", label: "High" }, { value: "urgent", label: "Urgent / emergency" }] },
          { name: "description", label: "Details (when it started, access times…)", type: "textarea" },
        ]}
        onSubmit={async (v) => { await api.post("/renter/maintenance", v); toast.success("Repair reported to your landlord"); reload(); }} />
    </div>
  );
}

function Docs() {
  const { data } = useFetch("/renter/documents");
  return (
    <div data-testid="renter-docs-page">
      <PageHeader eyebrow="Documents" title="Your documents" subtitle="Tenancy agreement, certificates and anything your landlord shares." />
      {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={FileText} title="No documents shared yet" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.map((d) => (
            <button key={d.id} onClick={() => openFile(`/renter/documents/${d.id}/download`).catch((e) => toast.error(errMsg(e)))}
              className="panel lift text-left flex items-center gap-4" data-testid={`renter-doc-${d.id}`}>
              <FileText className="w-8 h-8 text-emerald-700 shrink-0" />
              <div className="min-w-0 flex-1"><div className="font-semibold truncate">{d.name}</div><div className="text-xs text-slate-500 capitalize">{d.category} · {fmtDate(d.created_at)}</div></div>
              <Download className="w-4 h-4 text-slate-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RenterApp() {
  const { session } = useAuth();
  const items = [
    { id: "renter-home", to: "/portal", end: true, label: "My tenancy", icon: Home },
    { id: "renter-repairs", to: "/portal/repairs", label: "Repairs", icon: Wrench },
    { id: "renter-docs", to: "/portal/documents", label: "Documents", icon: FileText },
    { id: "renter-account", to: "/portal/account", label: "Account", icon: UserCircle },
  ];
  return (
    <Shell items={items} title={session.user.name} subtitle="Renter">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="repairs" element={<Repairs />} />
        <Route path="documents" element={<Docs />} />
        <Route path="account" element={<div><PageHeader eyebrow="Account" title="Your account" /><AccountCard /></div>} />
        <Route path="*" element={<Navigate to="/portal" replace />} />
      </Routes>
    </Shell>
  );
}
